import { logger } from "./LoggerService";
import { UpdateUserLocaleUseCase } from "@/application/use-cases/UserUseCases";
import { ApiUserRepository } from "@/infrastructure/repositories/ApiUserRepository";
import { keycloakAuthService } from "@/infrastructure/adapters/KeycloakAuthService";
import { addLocaleChangeListener } from "@/shared/composables/useLocaleChange";
/**
 * Service to synchronize locale changes with Keycloak
 * Listens to locale change events and automatically updates user locale in Keycloak
 */
class LocaleSyncService {
    constructor() {
        Object.defineProperty(this, "updateUserLocaleUseCase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cleanupListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /**
         * Handle locale change events
         */
        Object.defineProperty(this, "handleLocaleChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (locale, source) => {
                logger.debug(`LocaleSyncService: Locale change detected: ${locale} (source: ${source})`);
                if (!locale) {
                    logger.debug("LocaleSyncService: No locale in event detail");
                    return;
                }
                // Only sync if the source is 'manual' (user-initiated)
                if (source !== "manual") {
                    logger.debug(`LocaleSyncService: Locale change source is '${source}', skipping Keycloak sync for locale: ${locale}`);
                    return;
                }
                // Only sync if user is authenticated
                if (!keycloakAuthService.isAuthenticated()) {
                    logger.debug(`LocaleSyncService: User not authenticated, skipping Keycloak sync for locale: ${locale}`);
                    return;
                }
                try {
                    logger.debug(`LocaleSyncService: Syncing locale ${locale} to Keycloak...`);
                    // Update user locale in Keycloak via BFF
                    await this.updateUserLocaleUseCase.execute(locale);
                    logger.debug(`LocaleSyncService: Locale ${locale} synced to Keycloak successfully`);
                    // Force token refresh to get updated locale in token
                    await this.forceTokenRefresh();
                    logger.debug("LocaleSyncService: Token refreshed successfully with new locale");
                }
                catch (error) {
                    logger.debug("LocaleSyncService: Failed to sync locale with Keycloak:", error);
                    // Don't throw - we don't want to break the locale change flow
                    // The locale is already updated in the UI, just the Keycloak sync failed
                }
            }
        });
        const userRepository = new ApiUserRepository();
        this.updateUserLocaleUseCase = new UpdateUserLocaleUseCase(userRepository);
    }
    /**
     * Initialize the service and start listening to locale change events
     */
    initialize() {
        if (this.cleanupListener) {
            logger.debug("LocaleSyncService already initialized");
            return;
        }
        this.cleanupListener = addLocaleChangeListener(this.handleLocaleChange);
        logger.debug("LocaleSyncService initialized - listening for locale changes");
    }
    /**
     * Stop listening to locale change events and cleanup
     */
    dispose() {
        if (this.cleanupListener) {
            this.cleanupListener();
            this.cleanupListener = null;
            logger.debug("LocaleSyncService disposed");
        }
    }
    /**
     * Force refresh the Keycloak token to get updated locale
     */
    async forceTokenRefresh() {
        try {
            await keycloakAuthService.forceRefreshToken();
            logger.debug("LocaleSyncService: Keycloak token refreshed");
        }
        catch (error) {
            logger.debug("LocaleSyncService: Failed to refresh Keycloak token:", error);
            throw error;
        }
    }
}
// Export singleton instance
export const localeSyncService = new LocaleSyncService();
